import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { IntlProvider } from 'use-intl';
import { isLocale, type Locale } from '@/features/locale/supported-languages';
import { messages } from '@/i18n/messages';

const STORAGE_KEY = 'locale';

// Locale resolution replaces the Next.js cookie/accept-language flow
// (features/locale/{lang,actions}.ts): stored override → browser language →
// 'en'. Reads/writes are wrapped so a locked-down browser can't break render.
function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isLocale(stored)) return stored;
  } catch {
    // ignore storage errors
  }
  const nav =
    typeof navigator !== 'undefined'
      ? navigator.language.split('-')[0].trim()
      : 'en';
  return isLocale(nav) ? nav : 'en';
}

interface LocaleControl {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleControl | undefined>(undefined);

export function useLocaleControl(): LocaleControl {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocaleControl must be used within LocaleProvider');
  }
  return ctx;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore storage errors
    }
  }, []);

  const control = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <LocaleContext.Provider value={control}>
      <IntlProvider
        locale={locale}
        messages={messages[locale]}
        // Missing keys should not crash the customer-facing UI.
        onError={() => {}}
        getMessageFallback={({ key }) => key}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
